import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'projects/store/src/environments/environment';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ResponseHttpSuccess, UtilService } from 'library';
import { AuthService } from './api/v1/auth.service';
import { RequestService } from 'projects/store/src/app/services/api/v1/request.service';
import { firstValueFrom } from 'rxjs';

export interface ErrorCase {
  actor: string;
  categoria: string;
  evento: string;
  fecha: string;
  doc_identidad: string;
  numero_caso: string;
  imei: string;
  severidad: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorLoggingService {
  private readonly url = environment.salesAPIURL || "https://develop-panama-ventas-service-sfnpiy3jcq-ue.a.run.app/v1/";

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private utilService: UtilService,
    private requestService: RequestService
  ) { }

  /**
   * Genera un ID único en formato CASO-XXX-XXX
   * Usa números aleatorios de 1 a 999 divididos en dos grupos de 3 dígitos
   */
  generateCaseId(): string {
    // Generar dos números aleatorios entre 1 y 999 (3 dígitos máximo cada uno)
    const maxNumber = 999;
    const minNumber = 1;

    const part1 = Math.floor(Math.random() * (maxNumber - minNumber + 1)) + minNumber;
    const part2 = Math.floor(Math.random() * (maxNumber - minNumber + 1)) + minNumber;

    // Formatear a 3 dígitos con ceros a la izquierda si es necesario
    const formattedPart1 = part1.toString().padStart(3, '0');
    const formattedPart2 = part2.toString().padStart(3, '0');

    return `CASO-${formattedPart1}-${formattedPart2}`;
  }

  /**
   * Crea un caso de error con toda la información necesaria
   */
  createErrorCase(
    caseId: string,
    requestData?: any,
    responseData?: any,
    additionalData?: any
  ): ErrorCase {
    return {
      actor: "Flujo Universal (FE)",
      categoria: "Flujo Universal",
      evento: "Caso",
      fecha: new Date().toISOString(),
      doc_identidad: additionalData.docIdentidad || null,
      numero_caso: caseId,
      imei: "",
      severidad: additionalData.severidad,
      data: {
        url: additionalData?.url || '',
        api_request: this.sanitizeBody(requestData),
        api_response: this.sanitizeBody(responseData),
        message_front: additionalData.message,
        request_info: additionalData.requestInfo || null,
        request_id: additionalData.requestId || null,
        logOnly: additionalData.logOnly || false
      }
    };
  }

  /**
   * Guarda el caso de error en el backend
   */
  saveErrorCase(errorCase: ErrorCase): Observable<ResponseHttpSuccess> {
    return this.http.post(`${this.url}logs`, errorCase, {})
      .pipe(map(resp => resp as ResponseHttpSuccess));
  }

  /**
   * Sanitiza el body removiendo información sensible
   */
  private sanitizeBody(body: any): any {
    if (!body) return null;

    try {
      const sanitized = JSON.parse(JSON.stringify(body));
      const sensitiveKeys = ['password', 'token', 'secret', 'api_key', 'credit_card', 'cvv', 'pin'];

      const sanitizeObject = (obj: any) => {
        if (typeof obj !== 'object' || obj === null) return;

        Object.keys(obj).forEach(key => {
          if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
            obj[key] = '[REDACTED]';
          } else if (typeof obj[key] === 'object') {
            sanitizeObject(obj[key]);
          }
        });
      };

      sanitizeObject(sanitized);
      return sanitized;
    } catch (e) {
      return '[ERROR_PARSING_BODY]';
    }
  }

  async getRequestInfoIfExists(): Promise<any> {
    const requestId = this.utilService.getRequestIdFromUrl();

    if (requestId != null && requestId !== '') {
      try {
        const resp = await firstValueFrom(this.requestService.get(requestId));
        const requestInfo = resp.body.data || null;
        const docIdentidad = resp.body.data?.person?.document_id_number || null;

        if (!requestInfo && !docIdentidad) {
          return { requestInfo: null, docIdentidad: null, requestId };
        }

        return { requestInfo, docIdentidad, requestId };
      } catch (error) {
        console.warn('Error obteniendo request data:', error);
        return { requestInfo: null, docIdentidad: null, requestId };
      }
    }

    return null;
  }
}
